import React, { useState } from 'react';
import { PageHero } from '../components/PageHero';
import { Section } from '../components/Section';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { PlaceholderImage } from '../components/PlaceholderImage';
import { IconWrap } from '../components/IconWrap';
import { CiUser, CiSettings, CiCalendar, CiFileOn, CiBullhorn } from 'react-icons/ci';
import useTheme from '../hooks/useTheme';

export const Contact: React.FC = () => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    topic: '',
    message: '',
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    console.log('Contact form submission:', formData);
    setShowSuccess(true);
    setFormData({
      name: '',
      email: '',
      company: '',
      topic: '',
      message: '',
    });
    setTimeout(() => setShowSuccess(false), 5000);
  };

  return (
    <>
      <PageHero
        title="Contact"
        subtitle="Get in touch with the BCBH team."
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Contact' }]}
      />

      <Section>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '48px',
          }}
          className="contactGrid"
        >
          <div>
            <Card>
              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: 700,
                  color: 'var(--text)',
                  marginBottom: '24px',
                }}
              >
                Send us a message
              </h2>

              {showSuccess && (
                <div
                  style={{
                    padding: '16px',
                    backgroundColor: 'var(--g1)',
                    color: 'var(--text)',
                    borderRadius: 'var(--r1)',
                    marginBottom: '24px',
                  }}
                >
                  Thank you! Your message has been received. We'll get back to you soon.
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.text.primary,
                        marginBottom: '8px',
                      }}
                    >
                      Name <span style={{ color: 'var(--g1)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: theme.background.panel,
                        border: `1px solid ${errors.name ? 'red' : theme.colors.line}`,
                        borderRadius: theme.borderRadius.r1,
                        color: theme.text.primary,
                        fontSize: '16px',
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.name && (
                      <div style={{ color: 'red', fontSize: '13px', marginTop: '4px' }}>
                        {errors.name}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.text.primary,
                        marginBottom: '8px',
                      }}
                    >
                      Email <span style={{ color: 'var(--g1)' }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: theme.background.panel,
                        border: `1px solid ${errors.email ? 'red' : theme.colors.line}`,
                        borderRadius: theme.borderRadius.r1,
                        color: theme.text.primary,
                        fontSize: '16px',
                        fontFamily: 'inherit',
                      }}
                    />
                    {errors.email && (
                      <div style={{ color: 'red', fontSize: '13px', marginTop: '4px' }}>
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.text.primary,
                        marginBottom: '8px',
                      }}
                    >
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: theme.background.panel,
                        border: `1px solid ${theme.colors.line}`,
                        borderRadius: theme.borderRadius.r1,
                        color: theme.text.primary,
                        fontSize: '16px',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.text.primary,
                        marginBottom: '8px',
                      }}
                    >
                      Preferred Topic
                    </label>
                    <select
                      name="topic"
                      value={formData.topic}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: theme.background.panel,
                        border: `1px solid ${theme.colors.line}`,
                        borderRadius: theme.borderRadius.r1,
                        color: theme.text.primary,
                        fontSize: '16px',
                        fontFamily: 'inherit',
                      }}
                    >
                      <option value="">Select topic</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Event">Event</option>
                      <option value="Mission">Mission</option>
                      <option value="Opportunity">Opportunity</option>
                      <option value="Media">Media</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.text.primary,
                        marginBottom: '8px',
                      }}
                    >
                      Message <span style={{ color: 'var(--g1)' }}>*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={6}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        backgroundColor: theme.background.panel,
                        border: `1px solid ${errors.message ? 'red' : theme.colors.line}`,
                        borderRadius: theme.borderRadius.r1,
                        color: theme.text.primary,
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                      }}
                    />
                    {errors.message && (
                      <div style={{ color: 'red', fontSize: '13px', marginTop: '4px' }}>
                        {errors.message}
                      </div>
                    )}
                  </div>

                  <Button type="submit" variant="primary" style={{ width: '100%' }}>
                    Send Message
                  </Button>
                </div>
              </form>
            </Card>
          </div>

          <div>
            <Card style={{ marginBottom: '24px' }}>
              <h3
                style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: theme.text.primary,
                  marginBottom: '16px',
                }}
              >
                Typical Requests
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {[
                  { icon: <CiUser />, label: 'Partnerships & Introductions' },
                  { icon: <CiCalendar />, label: 'Events & Missions' },
                  { icon: <CiSettings />, label: 'Government Coordination' },
                  { icon: <CiFileOn />, label: 'Business Opportunities' },
                  { icon: <CiBullhorn />, label: 'Media Inquiries' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      backgroundColor: theme.background.panel,
                      borderRadius: theme.borderRadius.r1,
                      border: `1px solid ${theme.colors.line}`,
                    }}
                  >
                    <IconWrap icon={item.icon} size={20} color="primary" />
                    <span style={{ fontSize: '14px', color: theme.text.primary }}>{item.label}</span>
                  </div>
                ))}
              </div>
              <p
                style={{
                  fontSize: '12px',
                  color: theme.text.muted,
                  marginTop: '16px',
                  fontStyle: 'italic',
                }}
              >
                We aim to respond within 2–3 business days.
              </p>
            </Card>

            <Card style={{ marginBottom: '24px' }}>
              <h3
                style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: theme.text.primary,
                  marginBottom: '16px',
                }}
              >
                Cairo Office
              </h3>
              <p
                style={{
                  color: theme.text.muted,
                  lineHeight: 1.6,
                  marginBottom: '16px',
                }}
              >
                BCBH office at LOHMANN ECCE
              </p>
              <PlaceholderImage label="Cairo Office Map" height={200} />
            </Card>

            <Card>
              <h3
                style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: theme.text.primary,
                  marginBottom: '16px',
                }}
              >
                Busan Office
              </h3>
              <p
                style={{
                  color: theme.text.muted,
                  lineHeight: 1.6,
                  marginBottom: '16px',
                }}
              >
                BTEC chairman's company + BTP facility support
              </p>
              <PlaceholderImage label="Busan Office Map" height={200} />
            </Card>

            <Card style={{ marginTop: '24px' }}>
              <h3
                style={{
                  fontSize: '22px',
                  fontWeight: 600,
                  color: theme.text.primary,
                  marginBottom: '12px',
                }}
              >
                Email
              </h3>
              <a
                href="mailto:info@korea-egypt.com"
                style={{
                  color: theme.colors.green.g1,
                  textDecoration: 'none',
                  fontSize: '16px',
                }}
                className="emailLink"
              >
                info@korea-egypt.com
              </a>
            </Card>
          </div>
        </div>
      </Section>
    </>
  );
};

